import { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { useSettings } from '../settings/SettingsContext';
import { Loading, LoadError } from '../components/Loading';
import Drill from '../study/Drill';
import { validateFieldModes } from '../../../shared/api';
import type { CardResult } from '../../../shared/types';

/**
 * Everything in one language whose review interval has elapsed, most overdue
 * first.
 *
 * Only cards with a history appear. A word never answered is new, not overdue,
 * and padding this queue with unmet vocabulary would turn "catch up on what is
 * slipping" into "study the whole book" — see `is_due` on the server.
 *
 * The deck is fetched fresh rather than cached: what is due changes as soon as
 * you drill it, so a cached review would hand back cards already answered.
 */
export default function ReviewPage() {
  const { languageCode = '' } = useParams();
  const api = useApi();
  const { settings } = useSettings();

  const language = useAsync(() => catalogApi.language(languageCode, api), [languageCode, api]);
  const due = useAsync(() => progressApi.due(languageCode, undefined, api), [languageCode, api]);

  // Identity-stable so the drill's post-once effect does not re-fire.
  const record = useCallback(
    (results: { cardId: string; result: CardResult }[]) =>
      progressApi.recordReviewSession(results, api),
    [api],
  );

  if (language.loading || due.loading) {
    return (
      <main className="page">
        <Loading what="your review" />
      </main>
    );
  }

  if (language.error || !language.data || due.error || !due.data) {
    return (
      <main className="page">
        <LoadError
          message={language.error ?? due.error ?? 'Could not load your review'}
          onRetry={() => {
            language.reload();
            due.reload();
          }}
        />
      </main>
    );
  }

  const lang = language.data;
  const deck = due.data;

  // Nothing due is the good outcome, not an error — say so and offer the thing
  // worth doing instead.
  if (deck.cards.length === 0) {
    return (
      <main className="page page--narrow">
        <section className="summary">
          <h1>Nothing due</h1>
          <p className="muted">
            Everything you have studied in {lang.name} is still fresh. Reviews appear here as the
            gaps between them widen — a word answered right three times running is not asked again
            for a week.
          </p>
          <div className="summary-actions">
            <Link to={`/languages/${lang.code}`} className="btn btn--primary">
              Learn something new
            </Link>
            <Link to="/" className="btn">
              Back home
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const problem = validateFieldModes(settings.flashcards.fields, lang);
  if (problem) {
    return (
      <main className="page">
        <p className="error">{problem}</p>
        <Link to="/profile" className="btn">
          Back to the card setup
        </Link>
      </main>
    );
  }

  // `useDeckRun` fixes its order at mount and deliberately ignores a changed
  // deck prop — reordering cards under a cursor mid-run would be worse than
  // ignoring the change. So a refetched review has to remount rather than
  // re-render, and the deck's own contents are the honest key: answering cards
  // removes them from what is due, so a genuinely new review always differs.
  const deckKey = deck.cards.map((c) => c.id).join('|');

  return (
    <Drill
      key={deckKey}
      cards={deck.cards}
      language={lang}
      settings={settings}
      label={`${lang.name} review`}
      exitTo="/"
      exitLabel="Back home"
      deckNoun="review"
      onRecord={record}
      runNote={
        deck.totalDue > deck.cards.length ? (
          <p className="muted summary-run">
            {deck.totalDue - deck.cards.length} more still due — come back for another round.
          </p>
        ) : null
      }
      summaryActions={(finished) =>
        finished && (
          <button type="button" className="btn" onClick={() => due.reload()}>
            Fetch what is still due
          </button>
        )
      }
    />
  );
}
